import { useState } from "react";
import { Plus, KeyRound, Copy, Smartphone, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Pill, Table, Th, Td, Card } from "@/components/kit";
import { PasswordResetDialog } from "@/components/PlatformTools";

const ROLES = [
  ["staff", "Frontline staff — complete checks, log temps, report incidents"],
  ["chef", "Head chef / kitchen manager — plus checklists, equipment, HACCP"],
  ["manager", "General manager — plus team, venues, approvals"],
  ["auditor", "Auditor — read-only access to records & audit pack"],
  ["owner", "Owner — everything including billing"],
];
const RANK = { auditor: 0, staff: 1, chef: 2, manager: 3, owner: 4 };
const ADMIN_ROLES = ["owner", "manager"];
const LevelBadge = ({ role, id }) => {
  const admin = ADMIN_ROLES.includes(role);
  return <span data-testid={`member-level-${id}`} className={`mt-1 inline-block rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${admin ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{admin ? "Business Admin" : "Staff"}</span>;
};

export function StaffLinkCard() {
  const { business } = useAuth();
  const url = `${window.location.origin}/b/${business?.slug}`;
  return (
    <Card className="mb-8 bg-slate-950 text-white">
      <div className="flex flex-wrap items-center gap-4">
        <Smartphone className="h-8 w-8 text-emerald-400" />
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-400">Your staff app</div>
          <div data-testid="staff-app-url" className="mt-1 truncate font-mono text-sm">{url}</div>
          <div className="mt-1 text-xs text-slate-400">Open this on each kitchen device, add it to the home screen, and staff sign in with their PIN.</div>
        </div>
        <button data-testid="copy-staff-link-button" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }} className="ka-press inline-flex items-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold hover:bg-emerald-500"><Copy className="h-4 w-4" />Copy link</button>
      </div>
    </Card>
  );
}

export default function Team() {
  const { can, user } = useAuth();
  const { data, reload } = useApi("/team");
  const [form, setForm] = useState(null);
  const [pwFor, setPwFor] = useState(null);
  const admin = can("admin");
  const isOwner = user.tenant_role === "owner";
  const roles = ROLES.filter(([r]) => RANK[r] <= RANK[user.tenant_role] && (isOwner || !ADMIN_ROLES.includes(r)));
  const editable = (m) => admin && m.id !== user.id && RANK[m.tenant_role] <= RANK[user.tenant_role] && (isOwner || !ADMIN_ROLES.includes(m.tenant_role));

  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
    try {
      await api.post("/team", body);
      toast.success("Team member added");
      setForm(null);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const patch = async (m, body, msg) => {
    try {
      await api.patch(`/team/${m.id}`, body);
      toast.success(msg);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const resetPin = (m) => {
    const pin = window.prompt(`New 6-digit PIN for ${m.name}`);
    if (pin) patch(m, { pin }, "PIN updated");
  };

  return (
    <div>
      <PageHeader eyebrow="People" title="Team" subtitle="Staff don't take a seat. Give each person a role and a 6-digit PIN for the staff app."
        actions={admin && <button data-testid="add-member-button" onClick={() => setForm({ name: "", tenant_role: "staff", pin: "", email: "", password: "" })} className="ka-press inline-flex items-center gap-1 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700"><Plus className="h-4 w-4" />Add team member</button>} />
      {admin && <StaffLinkCard />}
      <p data-testid="access-levels-note" className="mb-4 text-sm text-slate-500"><b className="text-slate-700">Business Admin</b> (owner, manager) runs the business hub. <b className="text-slate-700">Staff</b> (chef, staff, auditor) keep records up to date. {isOwner ? "Only you, the owner, can promote or demote Business Admins." : "Only the owner can promote or demote Business Admins."}</p>
      <Table testid="team-table">
        <thead><tr><Th>Name</Th><Th>Role</Th><Th>Sign-in</Th><Th>Status</Th>{admin && <Th className="text-right">Actions</Th>}</tr></thead>
        <tbody>
          {(data || []).map((m) => (
            <tr key={m.id} data-testid={`member-row-${m.id}`}>
              <Td className="font-semibold text-slate-900">{m.name}{m.id === user.id && <span className="ml-2 text-xs text-slate-400">(you)</span>}</Td>
              <Td>
                {editable(m) ? (
                  <select data-testid={`member-role-${m.id}`} className="ka-input w-36 py-1.5" value={m.tenant_role} onChange={(e) => patch(m, { tenant_role: e.target.value }, "Role updated")}>{roles.map(([r]) => <option key={r} value={r}>{r}</option>)}</select>
                ) : <span className="capitalize">{m.tenant_role}</span>}
                <div><LevelBadge role={m.tenant_role} id={m.id} /></div>
              </Td>
              <Td className="text-xs">{m.email || "—"}{m.has_pin && <div className="text-emerald-700">PIN set</div>}</Td>
              <Td><Pill status={m.active ? "ok" : "low"} label={m.active ? "Active" : "Deactivated"} /></Td>
              {admin && (
                <Td className="whitespace-nowrap text-right">
                  {editable(m) && <>
                    <button data-testid={`reset-pin-${m.id}`} onClick={() => resetPin(m)} className="ka-press mr-1 inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold hover:border-slate-900"><KeyRound className="h-3 w-3" />PIN</button>
                    {isOwner && m.email && m.active && <button data-testid={`reset-password-${m.id}`} onClick={() => setPwFor(m)} className="ka-press mr-1 inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold hover:border-slate-900"><Lock className="h-3 w-3" />Password</button>}
                    <button data-testid={`toggle-active-${m.id}`} onClick={() => patch(m, { active: !m.active }, m.active ? "Deactivated" : "Reactivated")} className="ka-press rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold hover:border-slate-900">{m.active ? "Deactivate" : "Reactivate"}</button>
                  </>}
                </Td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      {pwFor && <PasswordResetDialog endpoint={`/team/${pwFor.id}/reset-password`} title={`Reset password · ${pwFor.name}`} onClose={() => setPwFor(null)} />}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Add team member</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <Field label="Name *"><input data-testid="member-name-input" required className="ka-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Role"><select data-testid="member-role-select" className="ka-input" value={form.tenant_role} onChange={(e) => setForm({ ...form, tenant_role: e.target.value })}>{roles.map(([r, d]) => <option key={r} value={r}>{d}</option>)}</select></Field>
              <Field label="6-digit PIN" hint="For quick sign-in on the staff app"><input data-testid="member-pin-input" inputMode="numeric" pattern="\d{6}" maxLength={6} className="ka-input font-mono tracking-[0.4em]" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })} /></Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Email (optional)"><input data-testid="member-email-input" type="email" className="ka-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Password (optional)"><input data-testid="member-password-input" type="password" minLength={8} className="ka-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
              </div>
              <p className="text-xs text-slate-500">Managers usually need an email and password for the desktop dashboard. Frontline staff only need a PIN.</p>
              <button data-testid="save-member-button" className="ka-press w-full rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700">Add to team</button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
